// Opening move: Define shared prosody types for stress data infrastructure
// These types power the NodeState-backed stress system across nodes, services, and plugins
// Big play: Keep types aligned with legacy system for smooth migration path

/**
 * Represents a single syllable within a word with stress marking
 *
 * Main play: Each syllable tracks its text, stress state, and metadata
 * Time to tackle the tricky bit: Confidence and override tracking enables hybrid auto/manual workflow
 * Epic 05 Scope 02: Added reasoning field for contextual override explanations
 */
export interface StressSyllable {
  /** The syllable text (e.g., "walk", "ing") */
  text: string
  /** Whether this syllable is stressed (legacy binary, use stressLevel for three-level) */
  stressed: boolean
  /** AI confidence in automatic marking (0-1, where 1 is highest confidence) */
  confidence: number
  /** Position within the word (0-based index) */
  position: number
  /** User manually set this stress (overrides auto-detection) */
  overridden: boolean
  /** Epic 05: Reasoning for contextual override (e.g., "contextual_noun", "verb_context") */
  reasoning?: string
  /** Epic 07: Three-level stress: 0=unstressed, 1=primary, 2=secondary */
  stressLevel?: 0 | 1 | 2
}

/**
 * Represents the complete stress pattern for a word
 *
 * Big play: Aggregates syllables and tracks whether user has made manual edits
 * Victory lap: Simple structure that serializes cleanly to JSON
 */
export interface StressPattern {
  /** Array of syllables with stress markings */
  syllables: StressSyllable[]
  /** True if any syllable in this word was manually overridden */
  overridden: boolean
  /** Whether this pattern was manually overridden by user (not auto-applied) */
  isManualOverride?: boolean
  /** ISO timestamp of when manual override was applied */
  overriddenAt?: string
}

/**
 * Result from stress analysis coordinator service
 *
 * Time to tackle the tricky bit: Includes source tracking and error handling
 * Main play: Services return this, plugins convert to StressPattern for storage
 * Epic 03: Removed 'heuristic' source, added 'backend-error' for failure states
 */
export interface StressPatternResult {
  /** The analyzed word (normalized) */
  word: string
  /** The detected stress pattern (null if analysis failed) */
  pattern: StressPattern | null
  /** Where the pattern came from */
  source: 'dictionary' | 'cache' | 'backend-error'
  /** Error message if analysis failed */
  error?: string
}

/**
 * Configuration for stress pattern state stored in NodeState
 *
 * Big play: Maps word keys to their stress patterns
 * Victory lap: Stored as plain object for clean JSON serialization
 */
export type StressPatternState = Record<string, StressPattern>

/**
 * Helper: Normalize a word to use as key in stress pattern storage
 *
 * Opening move: Strip punctuation, lowercase, and trim to get canonical form
 * This prevents "walking", "walking!", and "WALKING" from being treated as different words
 */
export function normalizeWordKey(word: string): string {
  return word.toLowerCase().replace(/[^a-z']/g, '')
}

/**
 * Helper: Create an empty stress pattern state (default value for NodeState)
 *
 * Main play: Returns empty object that serves as default when no stress data exists
 * Time to tackle the tricky bit: When serialized, Lexical omits default values to keep JSON lean
 */
export function createDefaultStressPatternState(): StressPatternState {
  return {}
}

/**
 * Helper: Check if stress pattern state has any data
 *
 * Victory lap: Useful for conditional rendering and JSON export optimization
 */
export function hasStressData(state: StressPatternState): boolean {
  return Object.keys(state).length > 0
}

// Victory lap: Types defined with helper utilities for clean stress data handling
// These power the headless infrastructure without touching DOM or rendering concerns
